import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { IDENTITY, METRICS, VALUE_PROP, OFFERS, TRUST, HERO_BG } from "../data/content";
import { SectionLabel, Reveal } from "../components/ui-bits";

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/55" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-slate-950/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-16 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-3xl"
          >
            <SectionLabel data-testid="hero-label">Transformation · Organisation · Excellence</SectionLabel>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-white leading-[1.05]">
              Voir juste,<br />
              <span className="text-blue-500">agir vite, délivrer.</span>
            </h1>
            <p data-testid="hero-title-role" className="mt-6 inline-flex items-center gap-2 font-heading text-lg md:text-xl font-semibold text-white border-l-2 border-blue-500 pl-4">
              Manager des Transitions
            </p>
            <p className="mt-6 text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl">
              {IDENTITY.name} — {IDENTITY.role}. Transformation des organisations sous
              contraintes : Santé, Éducation, Industrie, Services.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-lg font-medium transition-colors inline-flex items-center justify-center gap-2 group"
              >
                Discutons de votre mission
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/offres"
                data-testid="hero-cta-offers"
                className="border border-slate-700 hover:border-slate-500 text-white px-8 py-4 rounded-lg font-medium transition-colors inline-flex items-center justify-center"
              >
                Mes offres de service
              </Link>
            </div>
          </motion.div>

          {/* Metrics */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-800/60 border border-slate-800/60 rounded-2xl overflow-hidden"
          >
            {METRICS.map((m) => (
              <div key={m.label} data-testid="hero-metric" className="bg-slate-950/80 backdrop-blur p-6 md:p-8">
                <div className="font-heading text-3xl md:text-4xl font-semibold text-white">{m.value}</div>
                <div className="mt-2 text-sm text-slate-400">{m.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* VALUE PROP */}
      <section className="py-24 md:py-32 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionLabel>Ma proposition de valeur</SectionLabel>
            <h2 className="mt-6 font-heading text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white max-w-4xl leading-tight">
              {VALUE_PROP.intro}
            </h2>
          </Reveal>

          <div className="mt-16 grid md:grid-cols-3 gap-8">
            {VALUE_PROP.points.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div className="bg-slate-900/50 border border-slate-800 p-8 rounded-xl h-full hover:border-blue-500/30 hover:bg-slate-800/40 transition-colors">
                  <div className="w-12 h-12 rounded-lg bg-blue-600/10 border border-blue-500/20 flex items-center justify-center">
                    <p.icon className="text-blue-500" size={22} />
                  </div>
                  <h3 className="mt-6 font-heading text-xl font-medium text-white">{p.title}</h3>
                  <p className="mt-3 text-slate-400 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OFFERS PREVIEW */}
      <section className="py-24 md:py-32 bg-slate-900/30 border-y border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <SectionLabel>Mes offres de service</SectionLabel>
              <h2 className="mt-6 font-heading text-3xl md:text-4xl font-medium tracking-tight text-white">
                Trois familles d'expertise, des résultats mesurables.
              </h2>
            </div>
            <Link to="/offres" data-testid="offers-see-all" className="text-blue-400 hover:text-blue-300 font-medium inline-flex items-center gap-2 whitespace-nowrap">
              Tout voir <ArrowRight size={16} />
            </Link>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {OFFERS.map((o, i) => (
              <Reveal key={o.num} delay={(i % 3) * 0.08}>
                <div className="bg-slate-950/60 border border-slate-800 p-7 rounded-xl h-full hover:-translate-y-1 hover:border-blue-500/30 transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <o.icon className="text-blue-500" size={24} />
                    <span className="font-heading text-sm text-slate-600">{o.num}</span>
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-medium text-white leading-snug">{o.title}</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{o.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-24 md:py-28 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionLabel>Ils m'ont fait confiance</SectionLabel>
            <p className="mt-6 text-slate-400 max-w-2xl text-lg">
              Des organisations exigeantes qui m'ont confié des missions à fort enjeu.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-slate-800/60 border border-slate-800/60 rounded-2xl overflow-hidden">
              {TRUST.map((t) => (
                <div key={t.name} data-testid="trust-logo" className="bg-slate-950 px-4 py-8 flex items-center justify-center text-center">
                  <span className="font-heading text-sm md:text-base font-medium text-slate-300">{t.name}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-b from-slate-900/30 to-slate-950">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-white">
              Une organisation à transformer ?<br />Une transition à assurer ?
            </h2>
            <p className="mt-5 text-slate-400 text-lg">
              Disponibilité rapide et adaptable · mobilité nationale et internationale · missions de 3 à 12 mois.
            </p>
            <Link
              to="/contact"
              data-testid="bottom-cta"
              className="mt-8 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 rounded-lg font-medium transition-colors group"
            >
              Me contacter <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
